import React, { useEffect, useState, useRef } from "react";
import SessionManager from "services/auth/sessionManager";
import { useNavigate } from "react-router-dom";
import { AuthStorage } from "services/auth/AuthStorage";

/**
 * AuthGate Component
 * 
 * Purpose: Validates session on app load/reload
 * - If httpOnly cookies are missing/deleted manually -> logout
 * - If refresh token invalid -> logout
 * - If valid -> continue to app
 */
const AuthGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [ready, setReady] = useState(false);
  const ran = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    // StrictMode double invoke guard
    if (ran.current) return;
    ran.current = true;

    (async () => {
      try {
        // Check if user was logged in before
        const wasLoggedIn = AuthStorage.isLoggedIn();

        if (wasLoggedIn) {
          // Try to refresh token using httpOnly cookies
          const success = await SessionManager.bootstrap();

          if (!success) {
            // Refresh failed - httpOnly cookies missing/invalid
            // console.warn("Session validation failed - logging out");
            handleLogout();
          } else {
            // Session valid
            // console.log("Session validated successfully");
          }
        } else {
          // User was not logged in, clear any stale data
          SessionManager.clear();
        }
      } catch (error) {
        // Error during refresh - logout
        console.error("Session validation error:", error);
        handleLogout();
      } finally {
        setReady(true);
      }
    })();
  }, []);

  const handleLogout = () => {
    // Clear all auth data
    SessionManager.clear();
    AuthStorage.clear();

    // Clear redux persist if needed
    try {
      localStorage.removeItem("persist:cashquin");
    } catch {
      // Ignore
      const _ignore = true;
    }

    // Navigate to login
    setTimeout(() => {
      navigate("/login", { replace: true });
    }, 100);
  };

  // Show loader while validating
  if (!ready) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "18px",
          color: "#666",
        }}
      >
        Loading...
      </div>
    );
  }

  return <>{children}</>;
};

export default AuthGate;
